import classNames from 'classnames';
import React from 'react';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { useSelector, useDispatch } from 'react-redux';
import AssetActions from 'components/AssetActions';
import { useScrollIntoView } from 'components/AssetList/hooks';
import LoadingOverlay from 'components/LoadingOverlay';
import { ProcessType } from 'const';
import { addSelectedAssetPanelComponent, removeSelectedAssetPanelComponent } from 'containers/App/actions';
import { selectedComponentItem } from 'containers/App/selectors';
import { deleteComponent } from 'containers/Documents/actions';
import { loadLayoutDetailsMetadata } from 'containers/ModalWindows/LayoutDetails/actions';
import { setAssetIdToScroll as setAssetIdToScrollAction } from 'containers/ProjectPanel/actions';
import * as ProjectPanelModels from 'containers/ProjectPanel/models';
import { assetIdToScroll as assetIdToScrollSelector, selectLayoutCount } from 'containers/ProjectPanel/selectors';
import { Priority, useClickOutside } from 'hooks/useClickOutside';
import { ReusableLayout, ReusableLayoutMap, GroupLayoutDocumentMap, ScreenMap } from 'models';
import { LayoutListItemContent } from './LayoutListItemContent';
import styles from './styles.module.scss';
import { useLayoutListItemDragSource } from './useLayoutListItemDragSource';

type Props = {
  document: ReusableLayoutMap | GroupLayoutDocumentMap;
  storyCardId: string;
  screen?: ScreenMap;
  isReadOnly?: boolean;
  dataTestId?: string;
};

const LayoutListItem: React.FunctionComponent<Props> = (props) => {
  const {
    document,
    storyCardId,
    isReadOnly,
    screen,
    dataTestId = 'layoutListItem',
  } = props;

  const dispatch = useDispatch();
  const layoutId = document.get('id');
  const assetIdToScroll = useSelector(assetIdToScrollSelector);
  const layoutCount = useSelector(selectLayoutCount(layoutId));
  const isSelected = useSelector(selectedComponentItem(layoutId));
  const thumbnailUrl = document.get('_thumbnailUrl');
  const setAssetIdToScroll: ProjectPanelModels.ActionCreator.SetAssetIdToScroll = (id) => {
    return dispatch(setAssetIdToScrollAction(id));
  };
  const { elementRef } = useScrollIntoView(layoutId, assetIdToScroll, setAssetIdToScroll);
  const { container } = useClickOutside<HTMLDivElement>(Priority.SELECTION, {
    active: isSelected,
    onClickOutside: () => dispatch(removeSelectedAssetPanelComponent(layoutId)),
  });

  const { connectDragPreview, connectDragSource, isDragging, isGroupLayout, isLayoutBeingUploaded } = useLayoutListItemDragSource({
    document,
    storyCardId,
    screen,
    isReadOnly,
  });

  React.useEffect(() => { connectDragPreview(getEmptyImage(), { captureDraggingState: true }); }, [connectDragPreview]);

  const openPreview = (): void => {
    dispatch(loadLayoutDetailsMetadata(document.toJS() as ReusableLayout));
  };

  const deleteLayout = (): void => {
    const layoutIdToDelete = document.get('id');
    const entityType = document.get('entityType');
    dispatch(deleteComponent(layoutIdToDelete, entityType));
  };

  const onSelectHandler = (): void => {
    dispatch(addSelectedAssetPanelComponent(layoutId));
  };

  const isOutsideStoryCard = !storyCardId;

  return connectDragSource(
    (
      <div
        className={classNames(styles.LayoutListItem, {
          [styles.dragging]: isDragging,
          [styles.selected]: isSelected && isOutsideStoryCard,
        })}
        ref={elementRef}
        onMouseDown={onSelectHandler}
        data-testid={dataTestId}
      >
        <LoadingOverlay isLocked={isLayoutBeingUploaded} activeProcessType={ProcessType.DOCUMENT_UPLOADING} spinnerSize="md"
          dataTestId={`${dataTestId}-loadingOverlay`} />
        <LayoutListItemContent
          container={container}
          document={document}
          thumbnailUrl={thumbnailUrl}
          layoutCount={layoutCount}
          isOutsideStoryCard={isOutsideStoryCard}
          dataTestId={`${dataTestId}-content`}
          isGroupLayout={isGroupLayout}
        >
          {
            !isLayoutBeingUploaded &&
            <AssetActions
              onDelete={isOutsideStoryCard ? deleteLayout : undefined}
              onPreviewOpen={openPreview}
              hasDelete={!isReadOnly}
              dataTestId={`${dataTestId}-content-actions`}
            />
          }
        </LayoutListItemContent>
      </div>
    ),
  );
};

export default LayoutListItem;
